import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { currency, relTime } from "@/lib/format";
import { Wallet, TrendingUp, Building2, User2 } from "lucide-react";

export default function Payments() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sum, setSum] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/payments/summary");
      setSum(data);
    })();
  }, []);

  if (!sum) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-3xl text-slate-900">Payments</h1>
        <p className="text-sm text-slate-500 mt-1">
          {user?.role === "facility_incharge" ? "Your payment ledger across tickets." : "Complete facility-ops payment ledger across the business."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="col-span-1 md:col-span-2 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white p-6">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 border border-white/20 px-2 py-0.5 text-[11px] font-semibold">
            <TrendingUp className="w-3 h-3" /> Grand total
          </div>
          <div className="mt-3 font-display font-bold text-5xl tabular-nums">{currency(sum.grand_total)}</div>
          <div className="mt-1 text-sm text-white/80">across {sum.count} payment{sum.count === 1 ? "" : "s"}</div>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
            <Wallet className="w-3 h-3" /> Recent
          </div>
          <div className="mt-3 font-display font-bold text-3xl text-slate-900 tabular-nums">{sum.recent.length}</div>
          <div className="text-sm text-slate-500">last {sum.recent.length} entries</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <User2 className="w-4 h-4 text-slate-500" />
            <h3 className="font-semibold text-slate-800 text-sm">By Facility Incharge</h3>
          </div>
          {sum.by_facility_incharge.length === 0 ? <div className="text-xs text-slate-500">No data</div> : (
            <ul className="divide-y divide-slate-100">
              {sum.by_facility_incharge.map((r) => (
                <li key={r.id} className="py-2.5 flex items-center justify-between">
                  <div className="font-medium text-slate-800 text-sm">{r.name}</div>
                  <div className="font-semibold text-slate-900 tabular-nums">{currency(r.total)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-slate-500" />
            <h3 className="font-semibold text-slate-800 text-sm">By Outlet</h3>
          </div>
          {sum.by_outlet.length === 0 ? <div className="text-xs text-slate-500">No data</div> : (
            <ul className="divide-y divide-slate-100">
              {sum.by_outlet.map((r) => (
                <li key={r.id} className="py-2.5 flex items-center justify-between">
                  <div className="font-medium text-slate-800 text-sm">{r.name}</div>
                  <div className="font-semibold text-slate-900 tabular-nums">{currency(r.total)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 text-sm mb-3">Recent payments</h3>
        {sum.recent.length === 0 ? <div className="text-xs text-slate-500">No payments logged yet.</div> : (
          <ul className="divide-y divide-slate-100">
            {sum.recent.map((p) => (
              <li key={p.id} data-testid={`pay-row-${p.id}`}
                onClick={() => nav(`/tickets/${p.ticket_id}`)}
                className="py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50/60 rounded-lg px-2 -mx-2 transition-colors">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 text-sm truncate">{currency(p.amount)}</div>
                  <div className="text-[11px] text-slate-500">{p.logged_by_name} · {relTime(p.created_at)}</div>
                  {p.note && <div className="text-xs text-slate-600 mt-0.5 truncate">{p.note}</div>}
                </div>
                <div className="text-xs text-indigo-600 font-semibold flex-shrink-0">View ticket →</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
